import { applicationSessionStorage } from '@application/application_storage_session';
import { useCallback, useMemo } from 'react';
import type { BasketStorageData } from './use_booking_basket';

interface BasketDataStorageSpec {
	receptionId: number;
}

export interface BasketDataStorageHook {
	clearBasketDataStorage(): void;
	getBasketDataStorage(): BasketStorageData;
	setBasketDataStorage(newUserData: Partial<BasketStorageData>): void;
}

const emptyBasketStorageData: BasketStorageData = {
	respondantType: null,
	asAgentFor: null,
	reasonForVisit: null,
	currentDate: null,
	timeSlotId: null,
	timeSlotLockId: null,
	timeSlotFilterResourceId: null,
	timeSlotFilterSpecialityId: null,
	timeSlotFilterUnitId: null,
	formRespondantId: null,
	rescheduleTicket: null,
	reschedulingReason: null,
	additionalParticipants: null,
};

export function useBasketDataStorage({ receptionId }: BasketDataStorageSpec): BasketDataStorageHook {
	const clearBasketDataStorage = useCallback(() => {
		const userData = applicationSessionStorage.get('bookingBasket') ?? {};
		delete userData[receptionId];
		applicationSessionStorage.set('bookingBasket', userData);
	}, [receptionId]);

	const getBasketDataStorage = useCallback((): BasketStorageData => {
		const userData = applicationSessionStorage.get('bookingBasket') ?? {};
		return userData[receptionId] ?? { ...emptyBasketStorageData };
	}, [receptionId]);

	const setBasketDataStorage = useCallback(
		(newUserData: Partial<BasketStorageData>) => {
			const userData = applicationSessionStorage.get('bookingBasket') ?? {};
			userData[receptionId] = {
				...getBasketDataStorage(),
				...newUserData,
			};
			applicationSessionStorage.set('bookingBasket', userData);
		},
		[getBasketDataStorage, receptionId],
	);

	return useMemo(
		() => ({
			clearBasketDataStorage,
			getBasketDataStorage,
			setBasketDataStorage,
		}),
		[clearBasketDataStorage, getBasketDataStorage, setBasketDataStorage],
	);
}
